import React from 'react';

import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import Radio from 'components/lib/Radio';
import { useAppSelector } from 'store/hook';
import { ReviewAlertPreference } from 'types/communities';

import CommunityReviewAlertModeFilters from './CommunityReviewAlertModeFilters';
import {
  CommunityReviewAlertMode,
  createCategoryAlertPreference,
  REVIEW_ALERT_MODE_OPTIONS,
  ReviewAlertFilterField,
} from './constants';
import { CommunityReviewAlertScopeSettings } from './types';

const Divider = styled.div`
  margin: 20px 0;
  height: 1px;
  background-image: linear-gradient(to right, #d7d7d7, rgba(255, 255, 255, 0));
`;

const AlertModeTitle = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const AlertModeDescription = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 10px;
`;

const AlertModeGroup = styled.div`
  margin-bottom: 20px;
`;

const CategorySettingsList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 435px;
`;

const CategorySettingsCard = styled.div`
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  padding: 10px;
  background-color: var(--background);
`;

const CategorySettingsHeader = styled.div`
  display: flex;
  align-items: center;
`;

const CategoryName = styled.span`
  font-size: 14px;
  line-height: 1.4;
`;

const CategoryFilters = styled.div`
  margin-top: 10px;
`;

type Props = {
  settings: CommunityReviewAlertScopeSettings;
  onAlertModeChange: (mode: number) => void;
  onOverallFilterChange: (field: ReviewAlertFilterField, value: ReviewAlertPreference[ReviewAlertFilterField]) => void;
  onCategoryToggle: (reviewCategoryId: number, checked: boolean) => void;
  onCategoryFilterChange: (
    reviewCategoryId: number,
    field: ReviewAlertFilterField,
    value: ReviewAlertPreference[ReviewAlertFilterField]
  ) => void;
};

const CommunityReviewAlertModeSettings = ({
  settings,
  onAlertModeChange,
  onOverallFilterChange,
  onCategoryToggle,
  onCategoryFilterChange,
}: Props) => {
  const reviewCategories = useAppSelector((state) => state.global.reviewCategories);

  return (
    <>
      <Divider />
      <AlertModeTitle>Alert Mode</AlertModeTitle>
      <AlertModeDescription>
        {settings.mode === CommunityReviewAlertMode.OVERALL
          ? 'Alert on any review matching sentiment regardless of category.'
          : 'Set different rules for each review category.'}
      </AlertModeDescription>
      <AlertModeGroup>
        <Radio.Group
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
          value={settings.mode ?? CommunityReviewAlertMode.OVERALL}
          onChange={(event: any) => onAlertModeChange(event.target.value)}
        >
          {REVIEW_ALERT_MODE_OPTIONS.map((option) => (
            <Radio className={'large'} key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </AlertModeGroup>
      {settings.mode === CommunityReviewAlertMode.CATEGORY ? (
        <CategorySettingsList>
          {reviewCategories.map((category) => {
            const categoryId = category.id;
            const isSelected = settings.selected_category_ids.includes(categoryId);
            const categorySettings =
              settings.category_settings[categoryId] ?? createCategoryAlertPreference(categoryId);

            return (
              <CategorySettingsCard key={category.id}>
                <CategorySettingsHeader>
                  <Checkbox
                    className="green"
                    checked={isSelected}
                    onChange={(event: any) => onCategoryToggle(categoryId, Boolean(event.target.checked))}
                  >
                    <CategoryName>{category.label}</CategoryName>
                  </Checkbox>
                </CategorySettingsHeader>
                {isSelected && (
                  <CategoryFilters>
                    <CommunityReviewAlertModeFilters
                      reviewCount={categorySettings.number_of_reviews}
                      sentiment={categorySettings.positive}
                      withinMonths={categorySettings.within_months}
                      onReviewCountChange={(value) => onCategoryFilterChange(categoryId, 'number_of_reviews', value)}
                      onSentimentChange={(value) => onCategoryFilterChange(categoryId, 'positive', value)}
                      onWithinMonthsChange={(value) => onCategoryFilterChange(categoryId, 'within_months', value)}
                    />
                  </CategoryFilters>
                )}
              </CategorySettingsCard>
            );
          })}
        </CategorySettingsList>
      ) : (
        <CommunityReviewAlertModeFilters
          reviewCount={settings.overall.number_of_reviews}
          sentiment={settings.overall.positive}
          withinMonths={settings.overall.within_months}
          onReviewCountChange={(value) => onOverallFilterChange('number_of_reviews', value)}
          onSentimentChange={(value) => onOverallFilterChange('positive', value)}
          onWithinMonthsChange={(value) => onOverallFilterChange('within_months', value)}
        />
      )}
    </>
  );
};

export default CommunityReviewAlertModeSettings;
